import { useRouter } from '@bprogress/next';
import { CountryDropdown } from '@c/forms/CountryDropdown';
import FormInput from '@c/forms/controls/FormInput';
import FormTextarea from '@c/forms/controls/FormTextarea';
import { SendIcon } from '@c/icons';
import Button from '@ui/Button';
import Input from '@ui/Input';
import Select from '@ui/Select';
import { functions } from '@util/firebase';
import { isMobile } from '@util/index';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { httpsCallable } from 'firebase/functions';
import {
  CountryCode,
  ParseError,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min';
import { useEffect, useState } from 'react';
import BaseModal from './BaseModal';

const issueTypes = [
  { id: '2', value: 'buying', label: 'Buying' },
  { id: '3', value: 'selling', label: 'Selling' },
  { id: '4', value: 'tracking_shipment', label: 'Tracking Shipment' },
  { id: '5', value: 'payment', label: 'Payment' },
  { id: '6', value: 'other', label: 'Other' },
];
type IssueType =
  | 'buying'
  | 'selling'
  | 'tracking_shipment'
  | 'payment'
  | 'other';

type SelectedIssueType = {
  label: string;
  value: IssueType;
};

interface ChatModalProps {
  isOpen: boolean;
  dismiss: () => void;
  productId?: string;
}

function RequestCallModal({ isOpen, dismiss }: ChatModalProps) {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const router = useRouter();

  const [name, setName] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string>('US');
  const [orderNumber, setOrderNumber] = useState('');
  const [issueType, setIssueType] = useState<SelectedIssueType>();
  const [issueDescription, setIssueDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (userDoc?.phone && phoneNumber === null) {
      setPhoneNumber(userDoc.phone);
    }
    if ((userDoc?.first || userDoc?.last) && name === null) {
      setName(`${userDoc.first} ${userDoc.last}`);
    }
  }, [name, phoneNumber, userDoc]);

  async function requestPhoneSupport(userData: {
    name: string | null;
    phoneNumber: string | null;
    orderNumber: string;
    issueType: string;
    issueDescription: string;
    email?: string;
  }) {
    setErrors({});
    try {
      if (!userDoc) {
        router.push('/login');
        return;
      }
      let newErrors = {};
      if (!name) {
        newErrors = { ...newErrors, name: 'Please enter your name' };
      }
      if (!issueType?.value) {
        newErrors = { ...newErrors, issueType: 'Please select issue type' };
      }
      if (issueType?.value === 'other' && !issueDescription) {
        newErrors = {
          ...newErrors,
          issueDescription: 'Please enter issue description',
        };
      }
      if (orderNumber.length > 0 && !/^\d{5,}$/.test(orderNumber)) {
        newErrors = {
          ...newErrors,
          orderNumber: 'Order numbers must be at least 5 digits long',
        };
      }
      setErrors({ ...newErrors });
      if (Object.values(newErrors).some(Boolean)) return;
      setLoading(true);

      const phoneNumber = parsePhoneNumberFromString(
        userData.phoneNumber ?? '',
        countryCode as CountryCode
      );
      if (!phoneNumber || !phoneNumber.isValid()) {
        showErrorToast('Please enter a valid phone number');
        return;
      }
      userData.phoneNumber = phoneNumber.number + '';

      const res = await httpsCallable<
        {
          name: string | null;
          phoneNumber: string | null;
          orderNumber: string | null;
          issueType: string | null;
          issueDescription: string | null;
          email?: string | null;
        },
        { status: number }
      >(
        functions,
        'v2_phoneSupport'
      )(userData);
      if (res.data.status === 200) {
        showSuccessToast('Request sent!');
        setLoading(false);
      } else {
        throw new Error('Failed to send request');
      }
    } catch (e) {
      if (e instanceof ParseError) {
        if (e.message === 'NOT_A_NUMBER') {
          showErrorToast('Please enter a valid phone number');
        } else if (e.message === 'INVALID_COUNTRY') {
          showErrorToast('Please select a valid country');
        } else if (e.message === 'TOO_SHORT') {
          showErrorToast('Phone number entered is too short');
        } else if (e.message === 'TOO_LONG') {
          showErrorToast('Phone number entered is too long');
        }
      } else {
        // Unknown error
        logError(e);
        showErrorToast(
          'Failed to send request. Please contact support through email or chat.'
        );
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={dismiss}
      title={'Request A Call'}
      isFullScreen={isMobile()}
      overflowType="visible"
    >
      <div className="relative flex w-full flex-col gap-[2.4rem] rounded-3xl p-8 ">
        <div className="w-full">
          <Input
            error={!!errors.name}
            value={name ?? ''}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your name"
            type="text"
          />
          {errors.name && (
            <p className="text-[1.4rem] text-red-500">{errors.name}</p>
          )}
        </div>
        <div className="w-full">
          <Input
            error={!!errors.orderNumber}
            value={orderNumber}
            onChange={(e) => {
              if (errors.orderNumber) {
                setErrors((prev) => ({ ...prev, orderNumber: '' }));
              }
              setOrderNumber(e.target.value);
            }}
            placeholder="Order Number (Optional)"
            type="text"
          />
          {errors.orderNumber && (
            <p className="text-[1.4rem] text-red-500">{errors.orderNumber}</p>
          )}
        </div>
        <div className="w-full">
          <Select
            error={!!errors.issueType}
            placeholder={issueType ? issueType.label : 'Issue Type'}
            options={issueTypes}
            value={issueType?.value}
            onChange={(selectedOption) => {
              setErrors((prev) => ({ ...prev, issueType: '' }));
              setIssueType({
                label: selectedOption.label ?? '',
                value: selectedOption.value,
              });
            }}
            inModal // Pass the inModal prop to handle dropdown positioning
          />
          {errors.issueType && (
            <p className="text-[1.4rem] text-red-500">{errors.issueType}</p>
          )}
        </div>
        <div className="w-full">
          <FormTextarea
            value={issueDescription}
            error={!!errors.issueDescription}
            onChange={(e) => {
              if (errors.issueDescription) {
                setErrors((prev) => ({ ...prev, issueDescription: '' }));
              }
              setIssueDescription(e.target.value);
            }}
            placeholder="Issue Description"
          />
          {errors.issueDescription && (
            <p className="text-[1.4rem] text-red-500">
              {errors.issueDescription}
            </p>
          )}
        </div>
        <div className="w-full">
          <div className=" flex w-full gap-[1.6rem]">
            <CountryDropdown value={countryCode} setValue={setCountryCode} />
            <FormInput
              placeholder="Enter your phone number"
              type="tel"
              value={phoneNumber ?? ''}
              onChange={(e) => setPhoneNumber(e.target.value)}
              autoComplete="tel"
            />
          </div>
        </div>
        <div className="mt-[5rem] flex w-full items-center justify-center gap-[1.6rem] font-semibold text-brand-secondary">
          {loading ? (
            <Button
              type="secondary"
              text="Loading..."
              loading={loading}
              width="fluid"
              disabled
            />
          ) : (
            <Button
              type="secondary"
              text="Send"
              leadingIcon={<SendIcon />}
              width="fluid"
              onClick={() =>
                requestPhoneSupport({
                  name,
                  phoneNumber,
                  orderNumber,
                  issueType: issueType?.value ?? '',
                  issueDescription,
                  email: userDoc?.email,
                })
              }
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}

export default RequestCallModal;
